import { ActionIcon, Group, Tabs, Text } from '@mantine/core';
import { IoCloseSharp } from 'react-icons/io5';
import {
  PanelBody,
  PanelHeader,
  PanelRoot,
  StyledScrollArea,
  TabsContainerStyles,
  TabsPanelStyles,
} from './styles';
import { t } from 'i18next';
import { useEffect, useRef, useState } from 'react';

export type PanelTabsLayoutTab = {
  value: string;
  label: string;
  content: React.ReactNode;
};

export type PanelTabsLayoutProps = {
  title: React.ReactNode;
  leftIcon?: React.ReactNode;
  onClose?: () => void;
  tabs: PanelTabsLayoutTab[];
  defaultTab?: string;
};

export const PanelTabsLayout = ({
  title,
  leftIcon,
  onClose,
  tabs,
  defaultTab,
}: PanelTabsLayoutProps) => {
  const initialTab = defaultTab ?? tabs[0]?.value;
  const [activeTab, setActiveTab] = useState(initialTab);
  const lastDefaultTabRef = useRef(defaultTab);

  useEffect(() => {
    if (!defaultTab || defaultTab === lastDefaultTabRef.current) {
      return;
    }

    lastDefaultTabRef.current = defaultTab;
    setActiveTab(defaultTab);
  }, [defaultTab]);

  useEffect(() => {
    if (!tabs.some((tab) => tab.value === activeTab)) {
      setActiveTab(defaultTab ?? tabs[0]?.value);
    }
  }, [tabs, activeTab, defaultTab]);

  return (
    <PanelRoot>
      <PanelHeader>
        <Group position="apart" sx={{ flex: 1 }}>
          <Group spacing="md">
            {leftIcon}
            <Text fz="sm" fw={500}>
              {title}
            </Text>
          </Group>
          {onClose && (
            <ActionIcon
              variant="subtle"
              radius="md"
              size="sm"
              onClick={onClose}
              aria-label={t('graphEditor.panel.close')}
            >
              <IoCloseSharp size={14} />
            </ActionIcon>
          )}
        </Group>
      </PanelHeader>
      <PanelBody>
        <StyledScrollArea scrollbarSize={0}>
          <div
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <Tabs
              color="dark"
              value={activeTab}
              onTabChange={(value) => {
                if (value) setActiveTab(value);
              }}
              radius="xs"
              sx={TabsContainerStyles as any}
              styles={TabsPanelStyles as any}
            >
              <Tabs.List>
                {tabs.map((tab) => (
                  <Tabs.Tab key={tab.value} value={tab.value}>
                    {tab.label}
                  </Tabs.Tab>
                ))}
              </Tabs.List>
              {tabs.map((tab) => (
                <Tabs.Panel key={tab.value} value={tab.value} pt={0}>
                  {tab.content}
                </Tabs.Panel>
              ))}
            </Tabs>
          </div>
        </StyledScrollArea>
      </PanelBody>
    </PanelRoot>
  );
};
